/**
 * Tensiometer range (50 cbar vs 100 cbar).
 *
 * The two transducers are electrically identical apart from gain — same 200 mV
 * zero point, same ADC resolution, same response time — so the backend cannot
 * tell them apart from the readings alone. The range has to be declared when
 * the sensor is installed.
 *
 * 50 cbar is the implicit default: it was the only unit until 2026, so every
 * sensor already in production carries no sub_type at all and must keep
 * resolving to the original calibration. Only 100 cbar is ever persisted.
 */
const TENSIOMETER_SUB_TYPES = Object.freeze({
  FIFTY_CBAR: '50cbar',
  HUNDRED_CBAR: '100cbar',
});

// Single source of the human labels, so the editor's dropdown and the
// read-only sensor row can never drift apart.
const TENSIOMETER_RANGE_TITLES = Object.freeze({
  [TENSIOMETER_SUB_TYPES.FIFTY_CBAR]: '50 cbar',
  [TENSIOMETER_SUB_TYPES.HUNDRED_CBAR]: '100 cbar',
});

const TENSIOMETER_RANGE_OPTIONS = Object.entries(TENSIOMETER_RANGE_TITLES).map(
  ([value, title]) => ({ title, value }),
);

/**
 * Which option the editor opens on for an existing sensor. Anything that is
 * not an explicit 100cbar — including a missing sub_type — is a 50 cbar unit.
 */
const tensiometerRangeFromSubType = (subType) =>
  subType === TENSIOMETER_SUB_TYPES.HUNDRED_CBAR
    ? TENSIOMETER_SUB_TYPES.HUNDRED_CBAR
    : TENSIOMETER_SUB_TYPES.FIFTY_CBAR;

/**
 * What gets written back. Empty for 50 cbar, so sensors already in production
 * are never given a sub_type, and so moving a sensor back to 50 cbar clears
 * the one it had.
 */
const persistedTensiometerSubType = (chosenRange) =>
  chosenRange === TENSIOMETER_SUB_TYPES.HUNDRED_CBAR
    ? TENSIOMETER_SUB_TYPES.HUNDRED_CBAR
    : '';

/**
 * Human label for a stored sub_type, for read-only display. A sensor with no
 * sub_type is a 50 cbar unit, so this always resolves to one of the two ranges
 * rather than showing nothing.
 */
const tensiometerRangeTitle = (subType) =>
  TENSIOMETER_RANGE_TITLES[tensiometerRangeFromSubType(subType)];

module.exports = {
  TENSIOMETER_SUB_TYPES,
  TENSIOMETER_RANGE_TITLES,
  tensiometerRangeTitle,
  TENSIOMETER_RANGE_OPTIONS,
  tensiometerRangeFromSubType,
  persistedTensiometerSubType,
};
